import React, { Component } from 'react';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import CustomLoading from '../../custom/CustomLoading';
import { FormicaProps, withFormica } from '../../../helpers/withFormica';
import { WatchlistBaseModel } from 'domain/watchlist/WatchlistDomains';
import { RootState } from '../../../redux/store';
import { changeWatchlist } from '../../../redux/actions/WatchlistActions';
import { AlertStatus } from '../../../domain/watchlist/WatchlistAlertDomains';
import CustomPaginator, { CustomPaginatorProps } from '../../custom/CustomPaginator';
import _ from 'lodash';
import CustomTable, { CustomColumnProps } from '../../custom/CustomTable';
import CustomTableContentWrapper from '../../custom/CustomTableContentWrapper';
import moment from 'moment/moment';
import LanguageUtil from '../../../util/LanguageUtil';
import { DataTableRowClickEventParams } from 'primereact/datatable';
import NavigationUtil from '../../../util/NavigationUtil';

class WatchlistAlertHistoryComponent extends Component<Props, State> {
  public state = new State();

  public render() {
    const { requestLoading, rows, currentPage } = this.state;
    const { histories } = this.props;

    const data: WatchlistBaseModel[] = histories || [];
    const startIndex = (currentPage - 1) * rows;
    const paginatedData = data.slice(startIndex, startIndex + rows);

    if (requestLoading) {
      return <CustomLoading />;
    }
    if (data.length === 0) {
      return (
        <div className={'flex flex-col w-full h-full p-5'}>
          <p className='text-f-black-3'>
            {LanguageUtil.get({
              id: 'pages.alert.historyDetail.noData',
              defaultMessage: 'Veri bulunamadı. Lütfen geçmiş listesinden bir kayıt seçerek tekrar deneyin.',
            })}
          </p>
        </div>
      );
    }
    return (
      <div className={'flex flex-col w-full h-full p-5'}>
        <div className={'w-full flex flex-col max-h-[calc(100vh-20rem)] overflow-y-scroll'}>
          <CustomTable columns={this.getColumns()} value={paginatedData} loading={requestLoading} onRowClick={this.onRowClick} />
        </div>
        <div className={'absolute bottom-0 w-full'}>
          <CustomPaginator
            rows={rows}
            totalRecords={data.length}
            rowsPerPageOptions={[5, 10, 20, 50, 100, 200]}
            currentPage={currentPage}
            fixed={true}
            onPageChange={this.onPageChange}
          />
        </div>
      </div>
    );
  }

  private onPageChange = (event: CustomPaginatorProps) => {
    this.setState({ rows: event.rows, currentPage: event.page });
  };

  private onRowClick = (e: DataTableRowClickEventParams) => {
    const record: WatchlistBaseModel = e.data;
    const { alertId, type } = this.props;
    if (!record?.record?.id || !alertId || !type) return;
    const recordId = record.record.id;
    const urlParams = new URLSearchParams();
    urlParams.set('alertId', alertId);
    urlParams.set('recordId', recordId);
    urlParams.set('type', type);
    const url = `/${NavigationUtil.getTenant()}/watchlist-alert/detail/history?${urlParams.toString()}`;
    window.open(url, '_blank', 'noopener noreferrer');
  };

  private getColumns = (): CustomColumnProps[] => {
    const columns: CustomColumnProps[] = [
      {
        field: 'score',
        header: LanguageUtil.get({ id: 'pages.alert.table.score', defaultMessage: 'Score' }),
        alignHeader: 'left',
        columnKey: 'score',
        className: 'min-w-[50px]',
        body: (record: WatchlistBaseModel) => {
          return (
            <div
              className={`flex flex-col text-xs 2xl:text-md justify-center items-center ${this.getScoreItem(record?.score)} w-[40px] rounded-[50px] h-[22px]`}
            >
              {record?.score.toString()}
            </div>
          );
        },
      },
      {
        field: 'name',
        header: LanguageUtil.get({ id: 'pages.alert.table.name', defaultMessage: 'Name' }),
        alignHeader: 'left',
        columnKey: 'name',
        className: 'min-w-[250px]',
        body: (record: WatchlistBaseModel) => {
          return <div className={`flex w-full text-xs 2xl:text-md items-center text-f-black-3 justify-start relative group`}>{this.getName(record)}</div>;
        },
      },
      {
        field: 'categories',
        header: LanguageUtil.get({ id: 'pages.alert.table.category', defaultMessage: 'Category' }),
        alignHeader: 'left',
        columnKey: 'category',
        className: 'min-w-[220px]',
        body: (record: WatchlistBaseModel) => {
          return (
            <div className={'flex items-center gap-x-2'}>
              {this.getCategory(record)?.map((value: string, index: number) => {
                return (
                  <span
                    key={index}
                    className={`flex text-xs shrink-0 2xl:text-md h-full justify-center px-[16px] py-[3px] items-center rounded-[50px] bg-[#F6F6F9] text-[#4F4F4F]`}
                  >
                    {value}
                  </span>
                );
              })}
            </div>
          );
        },
      },
      {
        field: 'level',
        header: 'Level',
        alignHeader: 'left',
        columnKey: 'level',
        className: 'min-w-[150px]',
        body: (record: WatchlistBaseModel) => {
          return <div className={`flex w-full text-xs 2xl:text-md items-start text-f-black-3 justify-start relative group`}>{this.getLevelNames(record)}</div>;
        },
      },
      {
        field: 'status',
        header: LanguageUtil.get({ id: 'pages.alert.table.status', defaultMessage: 'Status' }),
        alignHeader: 'left',
        columnKey: 'status',
        className: 'min-w-[220px]',
        body: (record: WatchlistBaseModel) => {
          const status = (record as any)?.status as AlertStatus | undefined;
          if (!status) {
            return (
              <div className={`flex flex-col justify-center items-center text-xs 2xl:text-md rounded-[50px] bg-[#F6F6F9] text-[#4F4F4F] px-2 h-[22px]`}>
                {'N/A'}
              </div>
            );
          }
          return (
            <div className={`flex flex-col justify-center items-center text-xs 2xl:text-md rounded-[50px] ${this.getStatusItem(status)} px-2 h-[22px]`}>
              {this.getStatusText(status)}
            </div>
          );
        },
      },
      {
        field: 'timestamp',
        header: LanguageUtil.get({ id: 'pages.alert.table.createdAt', defaultMessage: 'Created At' }),
        alignHeader: 'left',
        columnKey: 'timestamp',
        className: 'min-w-[300px]',
        body: (record: WatchlistBaseModel) => {
          const createdAt: number | undefined = record?.timestamp;
          return (
            <div className={'flex items-start text-f-black-3 text-xs 2xl:text-md'}>
              <CustomTableContentWrapper text={createdAt == undefined ? '-' : moment(createdAt).format('LLLL')} />
            </div>
          );
        },
      },
    ];
    return columns;
  };

  private getName = (record: WatchlistBaseModel) => {
    const models = record?.record?.records || [];
    if (models.length == 0) return 'N/A';
    const watchlistRecord: any = models[0]?.watchlistRecord;
    if (!watchlistRecord?.names || watchlistRecord.names.length == 0) return 'N/A';
    const primary = watchlistRecord.names.find((n: any) => n.nameType == 'PRIMARY') || watchlistRecord.names[0];
    return primary?.value || 'N/A';
  };

  private getCategory = (record: WatchlistBaseModel) => {
    if (!record?.record?.records) return;
    const categories: any[] = [];
    record.record.records.forEach((response) => {
      if ((response as any)?.watchlistRecord?.categories) {
        categories.push((response as any).watchlistRecord.categories);
      }
    });
    if (categories.length == 0) return;
    return _.uniqBy([].concat(...categories));
  };

  private getLevelNames = (record: WatchlistBaseModel) => {
    const level = record.level;
    const type = record.record?.recordType;
    if (type == 'INDIVIDUAL') {
      switch (level) {
        case 1:
          return 'Identification';
        case 2:
          return 'Name';
        case 3:
          return 'Name, Date of Birth';
        case 4:
          return 'Name, Citizenship';
        case 5:
          return 'Name, Date of Birth, Citizenship';
        default:
          return 'N/A';
      }
    } else if (type == 'ENTITY') {
      switch (level) {
        case 1:
          return 'Identifier';
        case 2:
          return 'Name';
        case 3:
          return 'Name, Date of Registration';
        default:
          return 'N/A';
      }
    } else {
      return 'N/A';
    }
  };

  private getScoreItem = (score: number) => {
    if (score == 0) {
      return 'bg-[#DBF8E5] text-[#3F9E2C]';
    } else if (score > 0 && score < 100) {
      return 'bg-[#FCF1BB] text-[#C57500]';
    } else if (score >= 100) {
      return 'bg-[#FFF1F3] text-[#D2332F]';
    }
  };

  private getStatusItem = (status: AlertStatus | undefined) => {
    switch (status) {
      case AlertStatus.MATCH:
        return 'bg-[#FBE5E4] text-[#C34135]';
      case AlertStatus.NOT_MATCH:
        return 'bg-[#E8FBF1] text-[#027A48]';
      case AlertStatus.IN_REVIEW:
        return 'bg-[#DFEAFD] text-[#175CD3]';
      case AlertStatus.WAITING_FOR_REVIEW:
        return 'bg-[#F6F6F9] text-[#4F4F4F]';
    }
  };

  private getStatusText = (status: AlertStatus | undefined) => {
    if (status == undefined) return 'Unknown';
    switch (status) {
      case AlertStatus.MATCH:
        return LanguageUtil.get({ id: 'pages.alert.matched', defaultMessage: 'Marked as Matched' });
      case AlertStatus.NOT_MATCH:
        return LanguageUtil.get({ id: 'pages.alert.notMatched', defaultMessage: 'Marked as not Matched' });
      case AlertStatus.IN_REVIEW:
        return LanguageUtil.get({ id: 'pages.alert.inReview', defaultMessage: 'In Review' });
      case AlertStatus.WAITING_FOR_REVIEW:
        return LanguageUtil.get({ id: 'pages.alert.waitingForReview', defaultMessage: 'Waiting For Review' });
    }
    return status;
  };
}

interface Props extends FormicaProps, ReduxProps {
  histories: WatchlistBaseModel[] | undefined;
  alertId?: string | null;
  type?: string;
}

const mapStateToProps = (state: RootState) => ({
  watchlist: state.watchlist,
});
const mapDispatchToProps = { changeWatchlist };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

class State {
  requestLoading: boolean = false;
  rows: number = 10;
  currentPage: number = 1;
}

export default withFormica(connector(WatchlistAlertHistoryComponent));
